import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { SendTypeController } from "../../../../../core/controllers/send-type.controller";
import {
  ISendTypeConfiguration,
  SendTypeSendCategoryEnum
} from "../../../../../core/interfaces/send-type-configuration";
import { FlowService } from "../../../../../core/lib/flow.service";
import { ActivatedRoute, Router } from "@angular/router";
import { CompanyUiCustomizationService } from "src/app/core/lib/company-ui/company.ui-customization.service";

@Component({
  selector: "app-product-list",
  templateUrl: "./product-list.component.html",
  styleUrls: ["./product-list.component.scss"],
  providers: [SendTypeController]
})
export class ProductListComponent implements OnInit{

  SEND_PRODUCTS: ISendTypeConfiguration[] = [];

  control: FormControl = new FormControl(null, Validators.required);

  loadingList: boolean = false;

  alternativeTemplate: boolean = false;

  constructor(
    private _controller: SendTypeController,
    private cdr: ChangeDetectorRef,
    private flowService: FlowService,
    private route: ActivatedRoute,
    private router: Router,
    private companyUiService : CompanyUiCustomizationService) {
      
  }

  async ngOnInit(): Promise<void> {

        const BPERGroupUser = await this.companyUiService.BPERGroupUser();

        this.alternativeTemplate = BPERGroupUser;

  }

  get LETTER_PRODUCTS(): ISendTypeConfiguration[] {

    return this.SEND_PRODUCTS.filter((product: ISendTypeConfiguration) => [SendTypeSendCategoryEnum.Letter].includes(product.SendTypeSendCategory));

  }

  /**
   * Calcola il flex% per le card LETTER Products su desktop large (>1280px)
   * Distribuisce equamente lo spazio disponibile
   * @returns Percentuale di larghezza per ogni card (es: 33 per 3 card)
   */
  get LETTER_PRODUCT_CARD_MD_FLEX(): number {
    if (this.LETTER_PRODUCTS.length === 0) return 100;
    return Math.round(100 / this.LETTER_PRODUCTS.length);
  }

  /**
   * Primi 3 prodotti LETTER (per layout desktop medio 960-1280px con 4+ prodotti)
   * @returns Array con i primi 3 prodotti
   */
  get LETTER_PRODUCTS_FIRST_THREE(): ISendTypeConfiguration[] {
    return this.LETTER_PRODUCTS.slice(0, 3);
  }

  /**
   * Prodotti LETTER rimanenti dopo i primi 3 (per layout desktop medio con 4+ prodotti)
   * @returns Array con i prodotti dalla posizione 4 in poi
   */
  get LETTER_PRODUCTS_REMAINING(): ISendTypeConfiguration[] {
    return this.LETTER_PRODUCTS.slice(3);
  }

  /**
   * Calcola flex% per card LETTER rimanenti su desktop medio (960-1280px)
   * Distribuisce equamente lo spazio tra i prodotti rimanenti per riempire tutta la riga
   * @returns Percentuale di larghezza (100 / numero rimanenti)
   */
  get LETTER_REMAINING_FLEX(): number {
    const remaining = this.LETTER_PRODUCTS_REMAINING.length;
    if (remaining === 0) return 100;
    return Math.round(100 / remaining);
  }

  get DIGITAL_PRODUCTS(): ISendTypeConfiguration[] {

    return this.SEND_PRODUCTS.filter((product: ISendTypeConfiguration) => ![SendTypeSendCategoryEnum.Letter].includes(product.SendTypeSendCategory));

  }

  /**
   * Calcola il flex% per le card DIGITAL Products su desktop large (>1280px)
   * Distribuisce equamente lo spazio disponibile
   * @returns Percentuale di larghezza per ogni card (es: 50 per 2 card)
   */
  get DIGITAL_PRODUCT_CARD_MD_FLEX(): number {
    if (this.DIGITAL_PRODUCTS.length === 0) return 100;
    return Math.round(100 / this.DIGITAL_PRODUCTS.length);
  }

  /**
   * Primi 3 prodotti DIGITAL (per layout desktop medio 960-1280px con 4+ prodotti)
   * @returns Array con i primi 3 prodotti
   */
  get DIGITAL_PRODUCTS_FIRST_THREE(): ISendTypeConfiguration[] {
    return this.DIGITAL_PRODUCTS.slice(0, 3);
  }

  /**
   * Prodotti DIGITAL rimanenti dopo i primi 3 (per layout desktop medio con 4+ prodotti)
   * @returns Array con i prodotti dalla posizione 4 in poi
   */
  get DIGITAL_PRODUCTS_REMAINING(): ISendTypeConfiguration[] {
    return this.DIGITAL_PRODUCTS.slice(3);
  }

  /**
   * Calcola flex% per card DIGITAL rimanenti su desktop medio (960-1280px)
   * Distribuisce equamente lo spazio tra i prodotti rimanenti per riempire tutta la riga
   * @returns Percentuale di larghezza (100 / numero rimanenti)
   */
  get DIGITAL_REMAINING_FLEX(): number {
    const remaining = this.DIGITAL_PRODUCTS_REMAINING.length;
    if (remaining === 0) return 100;
    return Math.round(100 / remaining);
  }

  setValue(product: ISendTypeConfiguration) {

    if (this.control.value === product) return;

    if (this.flowService.activeFlow.SendTypeConfigurationSendTypeOid && this.flowService.activeFlow.SendTypeConfigurationSendTypeOid !== product.SendTypeOid) {

      this.flowService
        .openFlowAlertDialog()
        .afterClosed()
        .subscribe((result) => {

          if (result === true) {

            this.flowService
              .cancelFlow()
              .then(() => this.reloadCurrentRoute({ queryParams: { SendTypeOid: product.SendTypeOid } }));

          }

        });

    } else {

      this.control.setValue(product);

    }

  }

  private reloadCurrentRoute(extras = {}) {

    const url = this.router.url.split("?")[0];

    this.router.navigateByUrl(`/`, { skipLocationChange: true })
      .then(() => {

        this.router.navigate([`/${url}`], extras)
          .then(() => {
            console.log(`CT Gen: Reloaded ${this.router.url} route`);
          });

      });

  }

  /* A function that returns a promise. */
  getProducts = (): Promise<boolean> =>

    new Promise<boolean>((resolve) => {

      if (this.SEND_PRODUCTS.length > 0) {

        resolve(true);
        return;

      }

      this.loadingList = true;

      this.cdr.detectChanges();

      this._controller
        .getSendTypeConfigurationList()
        .then((result: ISendTypeConfiguration[]) => {

          this.SEND_PRODUCTS = result;

          this.route
            .queryParams
            .subscribe(qp => {

              if (qp["SendTypeOid"]) {

                const selectedProduct = this.SEND_PRODUCTS.find(product => product.SendTypeOid === +qp["SendTypeOid"]);

                if (selectedProduct) {

                  this.setValue(selectedProduct);

                }

              } else if (this.SEND_PRODUCTS.length === 1) {

                this.setValue(this.SEND_PRODUCTS[0]);

              }

            });

          this.loadingList = false;
          resolve(true);

        });

    });

}
